import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydButton from '../../../../commonDesign/Button'
import { dataPipelines } from '../../../../../api/coreData'
import { extractClientIdFromVariables } from '../utils/extractClientId'
import AceJsonReader from './AceJsonReader'
import CopyButton from './CopyButton'

// Color/border tokens — kept in makeStyles since opal utilities don't cover
// the var(--color-primary-...) / var(--color-success-...) palette directly.
const useStyles = makeStyles(() => ({
  callHeader: {
    flexShrink: 0,
    padding: '8px 12px',
    fontSize: 12,
    color: 'var(--color-primary-800, #1e40af)',
    backgroundColor: 'var(--color-primary-100, #dbeafe)',
    border: '1px solid var(--color-primary-200, #bfdbfe)',
    borderBottom: 'none',
    borderTopLeftRadius: 'var(--border-radius-sm, 4px)',
    borderTopRightRadius: 'var(--border-radius-sm, 4px)'
  },
  responseHeader: {
    flexShrink: 0,
    marginTop: 12,
    padding: '6px 10px',
    fontSize: 10,
    letterSpacing: '0.4px',
    backgroundColor: 'var(--color-success-100, #dcfce7)',
    color: 'var(--color-success-800, #166534)',
    border: '1px solid var(--color-success-200, #bbf7d0)',
    borderBottom: 'none',
    borderTopLeftRadius: 'var(--border-radius-sm, 4px)',
    borderTopRightRadius: 'var(--border-radius-sm, 4px)'
  },
  jsonWrap: {
    border: '1px solid var(--color-primary-200, #bfdbfe)',
    borderBottomLeftRadius: 'var(--border-radius-sm, 4px)',
    borderBottomRightRadius: 'var(--border-radius-sm, 4px)'
  }
}))

function formatErrorBody (error) {
  return JSON.stringify({
    error: error?.message || 'unknown',
    detail: error?.response?.data ?? null
  }, null, 2)
}

export default function OrionCallCard ({ step, entry }) {
  const classes = useStyles()
  const orion = step.orionConnect
  const clientId = extractClientIdFromVariables(entry?.variables)
  const { mutate, data: response, error: runError, isLoading: runLoading, reset } = dataPipelines.useExecuteOrionDomainQuery()

  // Drop any cached run when the card switches to a different entry/step so a
  // previous pipeline's response/error doesn't bleed under the new request.
  useEffect(() => { reset() }, [entry?.id, step.index, reset])

  const canRun = clientId != null
  const handleRun = () => {
    if (!canRun) return
    reset()
    mutate({ query: 'reporting/scope', clientId, request: orion.body })
  }

  return (
    <>
      <div className={`${classes.callHeader} flex items-center justify-between font-mono`}>
        <span>step #{step.index} — POST {orion.urlSuffix}</span>
        <span className='flex gap-sm'>
          <SydButton
            variant='outline'
            size='xs'
            onClick={handleRun}
            disabled={!canRun}
            processing={runLoading}
          >
            Run
          </SydButton>
          <CopyButton value={orion.body} label='Copy Body' />
        </span>
      </div>
      <div className={`${classes.jsonWrap} flex flex-col flex-1 min-h-0 overflow-hidden bg-white`}>
        <AceJsonReader
          name={`ddp-inspector-orion-${step.index}`}
          value={JSON.stringify(orion.body, null, 2)}
        />
      </div>

      {(response !== undefined || runError) && (
        <>
          <div className={`${classes.responseHeader} flex items-center justify-between font-mono uppercase font-bold`}>
            <span>response{runError ? ' — error' : ''}</span>
            {response !== undefined && <CopyButton value={response} label='copy response' />}
          </div>
          <div className={`${classes.jsonWrap} flex flex-col flex-1 min-h-0 overflow-hidden bg-white`}>
            <AceJsonReader
              name={`ddp-inspector-orion-response-${step.index}`}
              value={runError ? formatErrorBody(runError) : JSON.stringify(response, null, 2)}
            />
          </div>
        </>
      )}
    </>
  )
}

OrionCallCard.propTypes = {
  step: PropTypes.object.isRequired,
  entry: PropTypes.object
}
